import './DriverViewPage.css';
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Map, { FullscreenControl, useMap } from "react-map-gl";
import { VehicleState } from "../../models/VehicleState";
import { ActiveVehiclePlot } from "../Shared/ActiveVehiclePlot";
import { PlaybackClock } from '../Utils/PlaybackClock';
import { SpinnerLoading } from "../Utils/SpinnerLoading";
import { Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSatellite, faHome, faMap, faMagic, faBars, faChartLine, faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import { ViewControl } from './ViewControl';
import { CONFIG, getRestUrl } from "../../config";
import { useVehicleData } from '../../hooks/useVehicleData';
import { usePlayback } from "../../context/PlaybackContext";
import { useMapViewState } from '../../context/MapViewStateContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import mapboxgl from 'mapbox-gl';

// Configure Mapbox performance settings globally
mapboxgl.workerCount = 4;
mapboxgl.maxParallelImageRequests = 32;

export const DriverViewPage = () => {
  // Get the Vehicle ID from the URL in the window
  const vehicleId = (window.location.pathname).split('/')[2];

  const navigate = useNavigate();
  const { driverViewPageMap } = useMap();
  const mapboxToken = CONFIG.MAPBOX_TOKEN;

  // Constants
  const MAP_STYLE_SATELLITE = "mapbox://styles/tarterwaresteve/cm518rzmq00fr01qpfkvcd4md?optimize=true";
  const MAP_STYLE_STREET = "mapbox://styles/mapbox/standard";
  const MPS_TO_MPH = 2.236936;

  // States
  const [lastState, setLastState] = useState<VehicleState | null>(null);
  const [mapStyle, setMapStyle] = useState(MAP_STYLE_SATELLITE);
  const [isMapReady, setIsMapReady] = useState(false);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [showActiveVehiclePlot, setShowActiveVehiclePlot] = useState(false);
  const [goingHome, setGoingHome] = useState(false);
  const [hasSeenValidPosition, setHasSeenValidPosition] = useState(false);
  const [isSearchingSession, setIsSearchingSession] = useState(false);
  const [hasCheckedHistory, setHasCheckedHistory] = useState(false);

  // Driver view offset from straight ahead
  const [degViewOffset, setDegViewOffset] = useState(0);

  const { playbackOffset, setPlaybackSession, dataSource } = usePlayback();

  const {
    homeMapViewState,
    setHomeMapViewState
  } = useMapViewState();


  // Integrated Hook
  // Driver view usually wants a static size (e.g., 20) for calculation logic
  const {
    vehicleStateMap,
    isDataLoaded,
    setIsInterpolationEnabled,
    isInterpolationEnabled,
    version
  } = useVehicleData({
    vehicleSize: 20,
    intervalMs: 50
  });

  // Logic to find current vehicle
  const vehicleState = useMemo(() => {
    return vehicleStateMap.get(vehicleId) || lastState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, vehicleStateMap, lastState, version]);

  const missingTimestampRef = useRef<number | null>(null);

  const gotoHomePage = useCallback(() => {
    if (goingHome) return;
    setGoingHome(true);

    if (vehicleState && !(vehicleState.degLatitude === 0 && vehicleState.degLongitude === 0)) {
      setHomeMapViewState({
        ...homeMapViewState,
        longitude: vehicleState.degLongitude,
        latitude: vehicleState.degLatitude
      });
    }

    const provider = localStorage.getItem('roadrunner_map_provider') || 'google';
    navigate(provider === 'google' ? '/google/home' : '/home');
  }, [navigate, vehicleState, homeMapViewState, setHomeMapViewState, goingHome]);

  // Check if vehicle is absent in active tracking and query historical session if needed
  useEffect(() => {
    // Only query if basic data loaded, target vehicle is missing, and a search isn't already running
    if (!isDataLoaded || version === 0 || !vehicleId || vehicleStateMap.has(vehicleId) || isSearchingSession || goingHome) {
      if (isDataLoaded && version > 0 && vehicleStateMap.has(vehicleId)) {
        setHasCheckedHistory(true);
      }
      return;
    }

    async function fetchHistoricalSession() {
      setIsSearchingSession(true);
      try {
        const session = await fetchAuthSession(); //
        const accessToken = session.tokens?.accessToken?.toString(); //

        if (!accessToken) {
          console.error("Session expired"); //
          return;
        }

        const apiPath = dataSource === 'postgis' ? '/api/db-vehicle' : '/api/vehicle';
        const url = getRestUrl(`${apiPath}/get-vehicle-session/${vehicleId}`);
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include'
        });

        if (!res.ok) {
          throw new Error(`Session not found on server (status ${res.status})`);
        }

        const sessionData = await res.json();

        // Match playback window with the historical start point
        if (sessionData && sessionData.start) {
          const msEpochPlayback = new Date().getTime() - playbackOffset;

          // If there is no end time, but the current time is after the start,
          // don't mess with the clock
          if(!sessionData.end && (msEpochPlayback > sessionData.start)) {
            console.debug("Running simulation within our time: Not messing with time");
          }
          else if ((msEpochPlayback > sessionData.start) && (msEpochPlayback < sessionData.end)) {
            console.debug("Within start/end window: Not messing with time")
          }
          else {
            // Need to set map offset so that clock corresponds to playback Offset
            console.debug("Snapping playback clock to start of simulation session:", new Date(sessionData.start).toISOString());
            setPlaybackSession(sessionData.start);
          }
        }
      } catch (error) {
        console.error("Historical session lookup failed, exiting to home:", error);
        gotoHomePage(); // Fallback to home if simulation context cannot be resolved
      } finally {
        setIsSearchingSession(false);
        setHasCheckedHistory(true);
      }
    }

    fetchHistoricalSession();
  }, [isDataLoaded, vehicleStateMap, vehicleId, gotoHomePage, isSearchingSession, navigate, playbackOffset, setPlaybackSession, lastState, goingHome, version, dataSource]);


  // Handle Auto-Redirects as a Side Effect
  useEffect(() => {
    const msCurrentTime = Date.now() - playbackOffset;

    // Case 1: Data has gone stale
    if ((playbackOffset === 0) && lastState && (lastState.msEpochLastRun < msCurrentTime - (30 * 1000))) {
      gotoHomePage();
    }

    // Case 2: Explicit update for last state
    const currentState = vehicleStateMap.get(vehicleId);
    if (currentState && (lastState?.msEpochLastRun !== currentState.msEpochLastRun)) {
      setLastState(currentState);
    }
  }, [vehicleStateMap, vehicleId, lastState, playbackOffset, gotoHomePage]);

  const getCoordinateAtBearingAndRange = useCallback((degLatitude: number, degLongitude: number, degBearing: number, mRange: number) => {
    const KM_EARTH_RADIUS = 6378.14;
    const radLatitude = degLatitude / 180.0 * Math.PI;
    const radLongitude = degLongitude / 180.0 * Math.PI;
    const radBearing = degBearing / 180.0 * Math.PI;
    const kmRange = mRange / 1000.0;

    const radLatitudeDest = Math.asin(
      Math.sin(radLatitude) * Math.cos(kmRange / KM_EARTH_RADIUS) +
      Math.cos(radLatitude) * Math.sin(kmRange / KM_EARTH_RADIUS) * Math.cos(radBearing)
    );
    const radLongitudeDest = radLongitude + Math.atan2(
      Math.sin(radBearing) * Math.sin(kmRange / KM_EARTH_RADIUS) * Math.cos(radLatitude),
      Math.cos(kmRange / KM_EARTH_RADIUS) - Math.sin(radLatitude) * Math.sin(radLatitude)
    );
    const degLatitudeDest = radLatitudeDest / Math.PI * 180.0;
    const degLongitudeDest = radLongitudeDest / Math.PI * 180.0;

    return { lng: degLongitudeDest, lat: degLatitudeDest };
  }, []);

  const updateMapView = useCallback((data: VehicleState) => {
    const map = driverViewPageMap?.getMap();
    if (!data || !map || !isMapReady || !assetsLoaded) return;

    // Warping to (0, 0) signals loss of data feed
    if(data.degLatitude === 0 && data.degLongitude === 0) {
      if (hasSeenValidPosition) {
        gotoHomePage();
      }
    }

    const degViewBearing = data.degBearing + degViewOffset;
    driverViewPageMap?.setBearing(degViewBearing);

    const mRange = 20.0 * window.innerHeight / 932.0;
    const shiftedPoint = getCoordinateAtBearingAndRange(
      data.degLatitude,
      data.degLongitude,
      degViewBearing,
      mRange
    );

    // Ensure we aren't sending NaN or [0,0] to Mapbox
    if (shiftedPoint && !isNaN(shiftedPoint.lng) && !isNaN(shiftedPoint.lat)) {
      map.jumpTo({
        center: [shiftedPoint.lng, shiftedPoint.lat],
        bearing: degViewBearing,
        //animate: false // Critical for high-frequency updates
      })
    }
  }, [
    degViewOffset,
    driverViewPageMap,
    getCoordinateAtBearingAndRange,
    isMapReady,
    assetsLoaded,
    gotoHomePage,
    hasSeenValidPosition
  ]);

  // React to vehicle updates from the hook
  useEffect(() => {
    if (goingHome) return;

    if (vehicleState) {
      if (vehicleState.degLatitude === 0 && vehicleState.degLongitude === 0) {
        if (hasSeenValidPosition) {
          if (missingTimestampRef.current === null) {
            missingTimestampRef.current = Date.now();
          } else if (Date.now() - missingTimestampRef.current > 10000) {
            gotoHomePage();
          }
        }
      } else {
        setHasSeenValidPosition(true);
        missingTimestampRef.current = null; // Reset missing timer when valid
        updateMapView(vehicleState);
      }
    } else if (isDataLoaded && version > 0 && !isSearchingSession && hasCheckedHistory) {
      // The target vehicle went invalid / was not found
      if (missingTimestampRef.current === null) {
        missingTimestampRef.current = Date.now();
      } else if (Date.now() - missingTimestampRef.current > 10000) {
        gotoHomePage();
      }
    }
  }, [
    vehicleState,
    isDataLoaded,
    isSearchingSession,
    hasCheckedHistory,
    updateMapView,
    gotoHomePage,
    goingHome,
    version,
    hasSeenValidPosition
  ]);

  // Handle Model Injection & Style Switches
  useEffect(() => {
    const map = driverViewPageMap?.getMap();
    if (!map) return;

    const setupLayers = () => {
      // If the style isn't ready, wait 200ms and try again
      if (!map.isStyleLoaded()) {
        setTimeout(setupLayers, 200);
        return;
      }

      try {
        // Add Model
        if (!map.hasModel('mitsubishi-car')) {
          map.addModel('mitsubishi-car', '/models/mitsubishi/source/Untitled.glb');
        }

        // Add Source
        if (!map.getSource('vehicle-positions')) {
          map.addSource('vehicle-positions', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          });
        }

        // Add Layer
        if (!map.getLayer('vehicle-layer')) {
          map.addLayer({
            id: 'vehicle-layer',
            type: 'model',
            source: 'vehicle-positions',
            layout: {
              'model-id': 'mitsubishi-car'
            },
            paint: {
              'model-rotation': [
                0,
                0,
                ['+', ['get', 'bearing'], 180]
              ],
              'model-scale': [1, 1, 1],
              'model-type': 'common-3d',

              // Grab the color from the feature properties
              'model-color': ['get', 'vehicleColor'],
              // 1.0 = fully replace texture color, 0.7 = heavy tint
              'model-color-mix-intensity': 0.7
            }
          });
        }

        // Terrain & Fog
        if (!map.getSource('mapbox-dem')) {
            map.addSource('mapbox-dem', {
              type: 'raster-dem',
              url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
              tileSize: 512,
              maxzoom: 14
            });
            map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.0 });
        }

        // Set Fog to cull distant tiles and boost performance
        map.setFog({});

        // Disable raster fade-in transition animation to save GPU/CPU cycles
        const style = map.getStyle();
        if (style && style.layers) {
          style.layers.forEach((layer) => {
            if (layer.type === 'raster') {
              map.setPaintProperty(layer.id, 'raster-fade-duration', 0);
            }
          });
        }

        setIsMapReady(true);
        setAssetsLoaded(true);
      } catch (e) {
        console.error("Error loading 3D assets:", e);
      }
    };

    // Listen for style changes
    map.on('style.load', setupLayers);

    // Manually trigger if it's the first load or a style toggle
    setupLayers();

    return () => {
      map.off('style.load', setupLayers);
    };
  }, [driverViewPageMap, mapStyle]);

  // Update GeoJSON Source
  useEffect(() => {
    const map = driverViewPageMap?.getMap();
    if (!map || !isMapReady) return;

    const features = Array.from(vehicleStateMap.values())
      .filter((vState: any) => vState.id !== vehicleId)
      .map((vState: any) => ({
        type: 'Feature',
        properties: {
          id: vState.id,
          bearing: vState.degBearing,
          vehicleColor: vState.colorCode || '#FFFFFF'
        },
        geometry: {
          type: 'Point',
          coordinates: [
            vState.degLongitude,
            vState.degLatitude
          ]
        }
      }));

    const source: any = map.getSource('vehicle-positions');
    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    }
  }, [version, vehicleStateMap, driverViewPageMap, isMapReady, vehicleId]);

  const toggleMapStyle = useCallback(() => {
    setIsMapReady(false);
    setMapStyle(prevStyle => (prevStyle === MAP_STYLE_STREET ? MAP_STYLE_SATELLITE : MAP_STYLE_STREET));
  }, []);

  const managerHost = useMemo(() => {
    if (!vehicleState) return "";
    const host = vehicleState.managerHost;
    if (!host) return "";
    const lastDashIndex = host.lastIndexOf('-');
    return lastDashIndex >= 0 ? host.substring(lastDashIndex + 1) : host;
  }, [vehicleState]);

  const toggleShowActiveVehiclePlot = useCallback(() => {
    setShowActiveVehiclePlot(!showActiveVehiclePlot)
  }, [showActiveVehiclePlot]);

  // Show the map if we have isDataLoaded OR we have a lastState to show
  const shouldShowMap = (isDataLoaded || lastState) && vehicleState && !isSearchingSession;

  return (
    <div className="body row scroll-y">
      {shouldShowMap ? (
          <Map
            id="driverViewPageMap"
            mapStyle={mapStyle}
            mapboxAccessToken={mapboxToken}
            initialViewState={{
              zoom: 21,
              pitch: 84,
            }}
            pitch={80}
            zoom={22}
            minZoom={19}
            maxZoom={24}
          >
            <PlaybackClock />
            {/* --- Responsive Map Tools Container --- */}
            <div
              className="map-tools-container"
              style={{
                position: "absolute",
                top: "60px",
                right: "10px", // Pin to the right side (standard for map apps)
                display: "flex",
                flexDirection: "column", // Stacks the buttons vertically
                gap: "10px", // Adds consistent spacing between buttons
                zIndex: 1000, // Ensures they always float above the map canvas
              }}
            >
              <FullscreenControl />
              <Button
                variant="light"
                className="shadow-sm"
                onClick={gotoHomePage}
                title="Home"
              >
                <FontAwesomeIcon icon={faHome} />
              </Button>

               <Button
                 variant="light"
                 className="shadow-sm border border-primary animate-pulse"
                  onClick={() => {
                    const provider = localStorage.getItem('roadrunner_map_provider') || 'google';
                    navigate(provider === 'google' ? `/google/3d-view?vehicleId=${vehicleId}` : `/3d-view?vehicleId=${vehicleId}`);
                  }}
                 title="Switch to 3D Map View"
               >
                 <FontAwesomeIcon icon={faExchangeAlt} className="text-primary" />
               </Button>

              <Button
                variant="light"
                className="shadow-sm"
                onClick={toggleMapStyle}
              >
                {(mapStyle === MAP_STYLE_STREET) ?
                  <>
                    <FontAwesomeIcon title="Satellte Display" icon={faSatellite} className="mr-3" />
                  </>
                  :
                  <FontAwesomeIcon title="Map Display" icon={faMap} className="mr-3" />
                }
              </Button>

              <Button
                variant="light"
                className="shadow-sm"
                onClick={() => setIsInterpolationEnabled(!isInterpolationEnabled)}
              >
                <FontAwesomeIcon
                  icon={isInterpolationEnabled ? faMagic : faBars}
                  title={isInterpolationEnabled ? "Disable Smoothing" : "Enable Smoothing"}                    className="mr-3"
                />
              </Button>
              <Button
                variant="light"
                className="shadow-sm"
                onClick={() => setShowActiveVehiclePlot(!showActiveVehiclePlot)}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  title="Active Vehicle Plot"/>
              </Button>
            </div>
            <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "10rem" }}>

              <Card style={{
                width: '11rem',
                alignSelf: 'end',
                textAlign: 'center',
                borderTop: `6px solid ${vehicleState.colorCode || '#007bff'}`,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
              }}>
                <Card.Body>
                  <ViewControl degViewOffset={degViewOffset} setDegViewOffset={setDegViewOffset} />
                  <Card.Text style={{ fontSize: '1.1rem' }}>
                    <br/>
                    {`Vehicle:${vehicleState.id.substring(0, 8)}`}<br/>
                    {`${(Math.round(MPS_TO_MPH * vehicleState.metersPerSecond * 10) / 10)
                      .toFixed(1)
                      .padStart(4, ' ')}`} MPH<br />
                    {`${(Math.round(vehicleState.degBearing * 10) / 10)
                      .toFixed(1)
                      .padStart(5, ' ')}`}&deg;<br />
                    Host: {managerHost}<br />
                    {(vehicleState.nsLastExec / 1000000.0)
                      .toFixed(3)} ms<br />
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
            {showActiveVehiclePlot &&
              <ActiveVehiclePlot
                toggleShowActiveVehiclePlot={toggleShowActiveVehiclePlot}
                vehicleId={vehicleId}
              />
            }
          </Map>
      ) : (
          <SpinnerLoading />
      )}
    </div>
  )

}
